/**
 * Transient feedback, in BEETS' NotificationStack idiom.
 *
 * A write in BEST is not cheap - it re-derives the projection and proves the
 * file still regenerates before committing - so saying plainly whether it
 * happened matters more than it would in a CRUD app.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'

type Severity = 'success' | 'info' | 'warning' | 'error'

interface Toast {
  id: number
  message: string
  severity: Severity
}

interface ToastApi {
  notify: (message: string, severity?: Severity) => void
  succeeded: (message: string) => void
  failed: (message: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

// A failure is worth reading twice; a success is worth glancing at.
const DURATION: Record<Severity, number> = {
  success: 4000,
  info: 4000,
  warning: 8000,
  error: 10000,
}

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const value = useMemo<ToastApi>(() => {
    const notify = (message: string, severity: Severity = 'info') => {
      nextId += 1
      const toast = { id: nextId, message, severity }
      setToasts((current) => [...current, toast])
    }
    return {
      notify,
      succeeded: (message: string) => notify(message, 'success'),
      failed: (message: string) => notify(message, 'error'),
    }
  }, [])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Stack
        spacing={1}
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: (t) => t.zIndex.snackbar }}
      >
        {toasts.map((toast) => (
          <Snackbar
            key={toast.id}
            open
            autoHideDuration={DURATION[toast.severity]}
            onClose={(_event, reason) => {
              // Not on click-away: a message about a refused write should not
              // vanish because someone moved the mouse.
              if (reason !== 'clickaway') dismiss(toast.id)
            }}
            // Stacked by the parent, so each one sits where it is put.
            sx={{ position: 'static' }}
          >
            <Alert
              severity={toast.severity}
              variant="filled"
              onClose={() => dismiss(toast.id)}
              sx={{ width: '100%', maxWidth: 560 }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </Stack>
    </ToastContext.Provider>
  )
}

export function useToasts(): ToastApi {
  const value = useContext(ToastContext)
  if (!value) throw new Error('useToasts must be used inside a ToastProvider')
  return value
}
